/**
 * Catalog Registry
 * ================
 * Single source of truth for ALL reference catalogs (dictionaries) in the app:
 * catalog key → management route + permission + group + icon + description.
 *
 * Used by:
 *  - ReferenceSettingsView.vue (the "Reference Data" hub page: tiles grouped
 *    by business area, Workiz-style)
 *  - CatalogGearLink.vue  (admin gear icon next to catalog-fed selects,
 *    opens the management screen in a NEW tab)
 *  - readiness banners (Dashboard)
 *
 * `permission` mirrors the router meta.permission of the target route so a
 * tile / gear is only shown to users who can actually open the page.
 * A string means the user needs that permission; an array means ANY of them.
 *
 * Optional fields:
 *  - `groups`: extra group keys — the tile is shown in ALL listed groups in
 *    addition to its primary `group` (e.g. Tech Payment Methods appears in
 *    both Technician App and Finance).
 *  - `tags`: extra search keywords (synonyms users actually type/say) so the
 *    hub search finds the tile even when the label is worded differently.
 *  - `requiredSetup`: true when the catalog must be filled for a company to
 *    operate (matches backend READINESS_CHECKS / ONBOARDING_STEPS). The
 *    Reference Data hub shows a Required marker so owners and support agents
 *    know what can be skipped during onboarding.
 */

/** Ordered groups for the Reference Data hub page. */
export const CATALOG_GROUPS = [
  { key: 'company', title: 'Company', icon: 'mdi-office-building-cog-outline' },
  { key: 'jobs', title: 'Field Service & Jobs', icon: 'mdi-briefcase-outline' },
  { key: 'tech_app', title: 'Technician App', icon: 'mdi-cellphone-cog' },
  { key: 'territories', title: 'Territories & Skills', icon: 'mdi-map-outline' },
  { key: 'crm', title: 'Sales & CRM', icon: 'mdi-account-group' },
  { key: 'finance', title: 'Finance', icon: 'mdi-finance' },
  { key: 'hr', title: 'Team (HR)', icon: 'mdi-account-group-outline' },
  { key: 'inventory', title: 'Inventory & Assets', icon: 'mdi-package-variant-closed' },
  { key: 'callcenter', title: 'Contact Center', icon: 'mdi-headset' },
  { key: 'system', title: 'System', icon: 'mdi-cog-outline' },
]

export const CATALOGS = {
  // ── Company ──────────────────────────────────────────────────────────
  company_settings: {
    label: 'Company Settings',
    group: 'company',
    icon: 'mdi-domain',
    description: 'Company profile, region & locale, taxes, workflow mode',
    route: '/settings/domain',
    permission: 'settings.domain',
    requiredSetup: true,
  },
  branding: {
    label: 'Logo & Branding',
    group: 'company',
    icon: 'mdi-palette',
    description: 'Company logo and brand colors for the app header and customer emails',
    route: '/appearance',
    // Appearance is auth-gated; owners typically reach it via settings.domain.
    permission: 'settings.domain',
    requiredSetup: true,
  },

  // ── Field Service & Jobs ─────────────────────────────────────────────
  job_types: {
    label: 'Job Types',
    group: 'jobs',
    icon: 'mdi-format-list-bulleted-type',
    description: 'Types of work with colors, icons and durations',
    route: '/jobs/settings/job-types',
    permission: 'jobs.manage_settings',
    requiredSetup: true,
  },
  business_units: {
    label: 'Business Units',
    group: 'jobs',
    icon: 'mdi-domain',
    description: 'Trades / lines of business, geo coverage and branding',
    route: '/jobs/settings/business-units',
    permission: 'jobs.manage_settings',
    requiredSetup: true,
  },
  doc_templates: {
    label: 'Job Form Templates',
    group: 'jobs',
    icon: 'mdi-file-document-edit-outline',
    description: 'Which form sections each business unit shows on job creation',
    route: '/jobs/settings/doc-templates',
    permission: 'jobs.manage_settings',
  },
  cleaning_calculator: {
    label: 'Price & Duration Calculator',
    group: 'jobs',
    icon: 'mdi-calculator',
    description: 'Custom parameters, drag-and-drop formulas, rates and company calculator templates',
    route: '/jobs/settings/calculator',
    permission: 'jobs.manage_settings',
  },
  cleaning_operations: {
    label: 'Cleaning Operations',
    group: 'jobs',
    icon: 'mdi-broom',
    description: 'Walkthrough threshold, lockout policy, check-in verification, COI compliance',
    route: '/jobs/settings/cleaning-operations',
    permission: 'jobs.manage_settings',
    // Cleaning-only catalog (the "Cleaning Operations Walkthrough"), hidden by
    // the same per-company flag (TICK-00231). Never removed globally.
    cleaningOnly: true,
  },
  parts_markup: {
    label: 'Parts Markup',
    group: 'jobs',
    icon: 'mdi-cash-multiple',
    description: 'Percent markup per brand tier plus an optional flat amount per part (opt-in, off by default)',
    route: '/jobs/settings/parts-markup',
    permission: 'jobs.manage_settings',
  },
  parts_flows: {
    label: 'Parts Flows',
    group: 'jobs',
    icon: 'mdi-sitemap-outline',
    description: 'Build your own parts / materials / tools request lifecycle: stages, transitions, required fields, roles and automations. Save as a template and deploy to presets.',
    route: '/jobs/settings/parts-flows',
    permission: 'parts.manage_flows',
  },
  parts_pricing: {
    label: 'Parts Pricing',
    group: 'jobs',
    icon: 'mdi-tune-variant',
    description: 'Price parts by cost range: per-tier percentage plus optional fixed amount and price floor (opt-in, does not change current prices until wired in).',
    route: '/jobs/settings/parts-pricing',
    permission: 'parts.manage_pricing',
  },
  plan_vs_actual: {
    label: 'Plan vs Actual (Cleaning)',
    group: 'jobs',
    icon: 'mdi-chart-timeline-variant',
    description: 'Planned vs actual duration by cleaning type, cleaner, customer, first vs recurring',
    route: '/jobs/analytics/plan-vs-actual',
    permission: 'jobs.view',
    // Cleaning-only analytics — hidden with the other cleaning catalogs when a
    // non-cleaning company turns on "Hide cleaning-only reference catalogs"
    // (TICK-00230/00231). Never removed globally; cleaning companies keep it.
    cleaningOnly: true,
  },
  campaigns: {
    label: 'Campaigns',
    group: 'jobs',
    icon: 'mdi-bullhorn-outline',
    description: 'Marketing campaigns for job attribution',
    route: '/jobs/settings/campaigns',
    permission: 'jobs.manage_settings',
  },
  arrival_windows: {
    label: 'Arrival Windows',
    group: 'jobs',
    icon: 'mdi-clock-outline',
    description: 'Time windows offered to customers for arrival',
    route: '/jobs/settings/arrival-windows',
    permission: 'jobs.manage_settings',
    requiredSetup: true,
  },
  tags: {
    label: 'Tags',
    group: 'jobs',
    icon: 'mdi-tag-multiple-outline',
    description: 'Color-coded tags for jobs and customers',
    route: '/jobs/settings/tags',
    permission: 'jobs.manage_settings',
  },
  cancel_reasons: {
    label: 'Cancel Reasons',
    group: 'jobs',
    icon: 'mdi-cancel',
    description: 'Reasons available when cancelling a job',
    route: '/jobs/settings/cancel-reasons',
    permission: 'jobs.manage_settings',
  },
  job_statuses: {
    label: 'Job Statuses & Substatuses',
    group: 'jobs',
    icon: 'mdi-format-list-checks',
    description: 'Parent statuses (locked) and their editable substatuses with automations',
    route: '/settings/job-statuses',
    permission: 'workflow.view',
    tags: ['status', 'statuses', 'substatus', 'substatuses', 'crm status', 'job status',
      'статус', 'статусы', 'подстатус'],
  },
  cancel_refund_reasons: {
    label: 'Cancel / Refund Reasons',
    group: 'jobs',
    icon: 'mdi-cash-refund',
    description: 'Reason catalog shown when cancelling a job or issuing a refund',
    route: '/settings/job-statuses?tab=reasons',
    permission: 'workflow.view',
    tags: ['cancel reason', 'refund reason', 'refund', 'cancellation',
      'причина отмены', 'причина возврата', 'возврат'],
  },
  summary_templates: {
    label: 'Summary Templates',
    group: 'jobs',
    icon: 'mdi-file-document-multiple-outline',
    description: 'Reusable templates for job summaries',
    route: '/jobs/settings/summary-templates',
    permission: 'jobs.edit_summary_templates',
  },
  timesheet_codes: {
    label: 'Timesheet Codes',
    group: 'jobs',
    icon: 'mdi-clock-check-outline',
    description: 'Dispatch / timesheet event codes',
    route: '/jobs/settings/timesheet-codes',
    permission: 'jobs.manage_settings',
  },
  brands: {
    label: 'Brands',
    group: 'jobs',
    icon: 'mdi-star-circle-outline',
    description: 'Appliance / equipment brands with trade tags',
    route: '/jobs/settings/brands',
    permission: 'jobs.manage_settings',
    flowField: 'field.appliance_brands',
  },
  pricebook: {
    label: 'Pricebook',
    group: 'jobs',
    icon: 'mdi-book-open-variant',
    description: 'Services and materials with prices, by category',
    route: '/jobs/settings/pricebook',
    permission: 'finance.manage_pricebook',
  },
  equipment_types: {
    label: 'Equipment Types',
    group: 'jobs',
    icon: 'mdi-washing-machine',
    description: 'Equipment catalog with specifications and options',
    route: '/settings/references/equipment-types',
    permission: ['settings.references', 'admin.manage_settings'],
  },

  // ── Technician App ───────────────────────────────────────────────────
  fsa_interface: {
    label: 'Technician Interface',
    group: 'tech_app',
    icon: 'mdi-view-dashboard-edit-outline',
    description: 'Screen layouts and widgets of the technician app',
    route: '/settings/field-service',
    permission: 'settings.field_service',
  },
  activity_events: {
    label: 'Activity Events',
    group: 'tech_app',
    icon: 'mdi-calendar-clock',
    description: 'Technician time-tracking event types',
    route: '/settings/activity-events',
    permission: 'settings.field_service',
  },
  checklist_items: {
    label: 'Checklist Items',
    group: 'tech_app',
    icon: 'mdi-clipboard-check-outline',
    description: 'Job completion checklist items',
    route: '/settings/checklist-items',
    permission: 'settings.field_service',
  },
  tech_payment_methods: {
    label: 'Tech Payment Methods',
    group: 'tech_app',
    groups: ['finance'],
    icon: 'mdi-cash-multiple',
    description: 'Payment methods technicians see in the mobile app when collecting payment',
    route: '/settings/tech-payment-methods',
    permission: 'settings.field_service',
    tags: ['payment settings', 'payment options', 'payment methods', 'collect payment',
      'cash', 'check', 'credit card', 'payment link', 'mobile app payments',
      'technician payments'],
  },

  // ── Territories & Skills ─────────────────────────────────────────────
  regions: {
    label: 'Regions',
    group: 'territories',
    icon: 'mdi-map-marker-radius-outline',
    description: 'Geographic regions and their cities',
    route: '/production/regions',
    permission: 'territories.view_regions',
  },
  skills: {
    label: 'Skills',
    group: 'territories',
    icon: 'mdi-certificate',
    description: 'Technician skills tree with specifications',
    route: '/production/skills',
    permission: 'territories.view_skills',
  },
  projects_zones: {
    label: 'Projects & Zones',
    group: 'territories',
    icon: 'mdi-vector-polygon',
    description: 'Service zones drawn on the map, grouped by project',
    route: '/production/projects',
    permission: 'territories.view_projects',
  },
  technician_zones: {
    label: 'Technician Zones',
    group: 'territories',
    icon: 'mdi-account-arrow-right-outline',
    description: 'Technician-to-zone assignments',
    route: '/production/technician-zones',
    permission: 'territories.tz.view',
  },
  technician_form_settings: {
    label: 'Technician Form Rules',
    group: 'territories',
    icon: 'mdi-file-document-check-outline',
    description: 'Required documents by country and technician type',
    route: '/production/technicians/form-settings',
    permission: 'territories.technician_settings.manage',
  },

  // ── Sales & CRM ──────────────────────────────────────────────────────
  lead_sources: {
    label: 'Lead Sources',
    group: 'crm',
    icon: 'mdi-source-branch',
    description: 'Lead attribution sources and campaigns',
    route: '/crm/leads/sources',
    permission: 'crm.manage_sources',
  },
  lead_source_categories: {
    label: 'Source Categories',
    group: 'crm',
    icon: 'mdi-folder-outline',
    description: 'Categories grouping lead sources',
    route: '/crm/leads/categories',
    permission: 'crm.manage_sources',
  },
  lead_flows: {
    label: 'Lead Flows',
    group: 'crm',
    icon: 'mdi-sitemap-outline',
    description: 'Visual lead routing flows',
    route: '/crm/leads/flows',
    permission: 'crm.manage_sources',
  },
  call_tracking: {
    label: 'Call Tracking Numbers',
    group: 'crm',
    icon: 'mdi-phone-plus-outline',
    description: 'DNI tracking numbers and forwarding rules',
    route: '/crm/call-tracking',
    permission: 'tracking.view',
  },

  // ── Finance ──────────────────────────────────────────────────────────
  finance_settings: {
    label: 'Finance Settings',
    group: 'finance',
    icon: 'mdi-bank-outline',
    description: 'Legal entities, bank accounts, payment providers',
    route: '/accounting/settings',
    permission: 'finance.manage_settings',
  },
  taxes_billing: {
    label: 'Taxes & Billing',
    group: 'finance',
    icon: 'mdi-percent-outline',
    description: 'Tax mode: on top, included in price, or no taxes',
    route: '/accounting/settings?tab=taxes',
    permission: 'finance.manage_settings',
  },
  holiday_calendar: {
    label: 'Holiday Calendar',
    group: 'finance',
    icon: 'mdi-calendar-star',
    description: 'Statutory holidays by year and province',
    route: '/accounting/holiday-calendar',
    permission: ['accounting.manage_holidays', 'hr.employees'],
  },

  // ── Team (HR) ────────────────────────────────────────────────────────
  hr_positions: {
    label: 'Job Positions',
    group: 'hr',
    icon: 'mdi-badge-account-outline',
    description: 'Company positions with levels and flags',
    route: '/hr/positions',
    permission: 'hr.manage_positions',
  },
  hr_unit_types: {
    label: 'Org Unit Types',
    group: 'hr',
    icon: 'mdi-file-tree-outline',
    description: 'Types of organizational units (division, department…)',
    route: '/hr/unit-types',
    permission: 'hr.manage_settings',
  },
  hr_org_structure: {
    label: 'Org Structure',
    group: 'hr',
    icon: 'mdi-account-network-outline',
    description: 'Organizational units tree',
    route: '/hr/org-structure',
    permission: 'hr.org_structure',
  },
  hr_legal_structure: {
    label: 'Legal Structure',
    group: 'hr',
    icon: 'mdi-scale-balance',
    description: 'Legal entities and their addresses',
    route: '/hr/legal-structure',
    permission: 'hr.legal_structure',
  },
  hr_employees: {
    label: 'Employees',
    group: 'hr',
    icon: 'mdi-account-multiple-outline',
    description: 'Employee directory — at least one active employee is required (add technicians to dispatch jobs)',
    route: '/hr/employees',
    permission: 'hr.employees',
    requiredSetup: true,
  },

  // ── Inventory & Assets ───────────────────────────────────────────────
  parts_catalog: {
    label: 'Parts Catalog',
    group: 'inventory',
    icon: 'mdi-cog-outline',
    description: 'Parts and materials catalog',
    route: '/operations/catalog',
    permission: 'parts.view_catalog',
  },
  parts_suppliers: {
    label: 'Suppliers',
    group: 'inventory',
    icon: 'mdi-truck-outline',
    description: 'Parts suppliers and vendors',
    route: '/operations/suppliers',
    permission: 'parts.view_suppliers',
  },
  warehouses: {
    label: 'Warehouses',
    group: 'inventory',
    icon: 'mdi-warehouse',
    description: 'Physical, virtual and vehicle warehouses',
    route: '/operations/warehouses',
    permission: 'parts.view_inventory',
  },
  it_categories: {
    label: 'IT Categories',
    group: 'inventory',
    icon: 'mdi-desktop-classic',
    description: 'IT resource categories',
    route: '/settings/it-infrastructure/categories',
    permission: 'settings.it_infrastructure',
  },

  // ── Contact Center ───────────────────────────────────────────────────
  call_groups: {
    label: 'Call Groups',
    group: 'callcenter',
    icon: 'mdi-account-group',
    description: 'Operator groups for call distribution',
    route: '/call-center/groups',
    permission: 'call_groups.view',
  },
  routing_rules: {
    label: 'Routing Rules',
    group: 'callcenter',
    icon: 'mdi-call-split',
    description: 'Inbound call routing rules',
    route: '/call-center/routing',
    permission: 'routing_rules.view',
  },
  next_steps: {
    label: 'Next Steps',
    group: 'callcenter',
    icon: 'mdi-debug-step-over',
    description: 'Post-call actions (audio, SMS, transfers)',
    route: '/call-center/next-steps',
    permission: 'next_steps.view',
  },
  ivr_flows: {
    label: 'IVR Flows',
    group: 'callcenter',
    icon: 'mdi-phone-settings-outline',
    description: 'IVR call flows, schedules and audio library',
    route: '/call-center/ivr-constructor',
    permission: 'call_flows.view',
  },
  call_center: {
    label: 'Call Center Settings',
    group: 'callcenter',
    icon: 'mdi-headset',
    description: 'Phone numbers, caller IDs and user assignments',
    route: '/settings/call-center',
    permission: 'dialer.manage_settings',
  },

  // ── System ───────────────────────────────────────────────────────────
  roles: {
    label: 'Roles & Permissions',
    group: 'system',
    icon: 'mdi-shield-account-outline',
    description: 'RBAC roles and their permission sets',
    route: '/settings/roles',
    permission: 'settings.roles',
  },
  task_types: {
    label: 'Task Types',
    group: 'system',
    icon: 'mdi-format-list-checks',
    description: 'Task types with SLA, icons and teams',
    route: '/settings/task-types',
    permission: 'task_types.view',
  },
  industry_presets: {
    label: 'Industry Presets',
    group: 'system',
    icon: 'mdi-shape-outline',
    description: 'Industry templates that pre-fill reference data',
    route: '/settings/presets',
    permission: 'settings.administration',
  },
  customer_portal: {
    label: 'Customer Portal',
    group: 'system',
    icon: 'mdi-account-box-outline',
    description: 'Journey steps and notification templates',
    route: '/settings/customer-portal',
    permission: 'settings.administration',
  },
}

export function getCatalog(key) {
  return CATALOGS[key] || null
}

/**
 * Catalogs grouped for the Reference Data hub, filtered by a permission
 * checker (e.g. authStore.hasPermission). Returns only non-empty groups.
 */
export function getGroupedCatalogs(hasPermission) {
  return CATALOG_GROUPS.map(group => ({
    ...group,
    items: Object.entries(CATALOGS)
      .filter(([, c]) => c.group === group.key || (c.groups || []).includes(group.key))
      .filter(([, c]) => !hasPermission || hasPermission(c.permission))
      .map(([key, c]) => ({ key, ...c })),
  })).filter(group => group.items.length > 0)
}

/**
 * Flat, de-duplicated list of EVERY permission referenced by any catalog
 * (permission may be a string or an array). Used to open the Settings block
 * and the Reference Data hub to non-admins: whoever can access at least one
 * catalog should be able to reach the hub and see it there (their catalog
 * active, the rest greyed out). Kept in the registry so it stays the single
 * source of truth — add a catalog and the access automatically follows.
 */
export function getAllCatalogPermissions() {
  const set = new Set()
  for (const c of Object.values(CATALOGS)) {
    const p = c.permission
    if (Array.isArray(p)) p.forEach(x => x && set.add(x))
    else if (p) set.add(p)
  }
  return [...set]
}

/**
 * ALL catalogs grouped for the hub, each tile flagged `accessible` via the
 * permission checker. Unlike getGroupedCatalogs this hides NOTHING by
 * permission: the hub shows the full catalog landscape and greys out the tiles
 * the user cannot open (they still can't click into them). Groups keep
 * CATALOG_GROUPS order.
 *
 * `options.hideCleaning` (default false) drops the cleaning-only catalogs
 * (`cleaningOnly: true`) entirely, so a non-cleaning company (e.g. an
 * appliance-repair business) is not shown catalogs it never uses. This only
 * affects what the hub lists; the catalogs still exist for cleaning companies
 * (TICK-00230 / TICK-00231).
 */
export function getAllGroupedCatalogs(hasPermission, options = {}) {
  const { hideCleaning = false, fieldEnabled = null } = options
  return CATALOG_GROUPS.map(group => ({
    ...group,
    items: Object.entries(CATALOGS)
      .filter(([, c]) => c.group === group.key || (c.groups || []).includes(group.key))
      .filter(([, c]) => !(hideCleaning && c.cleaningOnly))
      // Company Flow: hide a catalog whose optional field the company turned off
      // (e.g. Brands for a cleaning company). No flowField / no checker = shown.
      .filter(([, c]) => !c.flowField || !fieldEnabled || fieldEnabled(c.flowField))
      .map(([key, c]) => ({
        key,
        ...c,
        accessible: !hasPermission || !!hasPermission(c.permission),
      })),
  })).filter(group => group.items.length > 0)
}
